<script setup>
import DOMPurify from 'dompurify';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { sleep } from '~/common/utils/common.utils';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  chartConfig: {
    type: Object,
    default: () => ({}),
  },
  widgetId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const bi_store = useBiStore();

const { $t } = useCommonImports();

const state = reactive({
  form_data: props.chartConfig,
  background_color: props.chartConfig?.background_color ?? '#FFFFFF',
});

async function onSave() {
  if (!DOMPurify.sanitize(state.form_data.content, { ALLOWED_TAGS: ['img'] }).trim())
    return;
  if (props.mode === 'create') {
    const uid = crypto.randomUUID();

    bi_store.createWidget(
      uid,
      {
        config: {
          query: {},
          chart: {
            type: 'rich_text',
            ...state.form_data,
            background_color: state.background_color,
          },
        },
      },
      { w: 5, h: 2 },
    );

    emit('close');
    await sleep(500);
    const el = document.querySelectorAll(`[uid='${uid}']`)?.[0];
    el?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }
  else {
    bi_store.updateWidget(props.widgetId, {
      config: {
        query: {},
        chart: {
          type: 'rich_text',
          ...state.form_data,
          background_color: state.background_color,
        },
      },
    });
    emit('close');
  }
}
</script>

<template>
  <HawkModalContainer content_class="!w-[900px]">
    <Vueform
      v-model="state.form_data"
      sync
      size="sm"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, label: 4, wrapper: 12 },
      }"
      :endpoint="onSave"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            <div class="flex flex-col justify-start">
              {{ props.mode === 'create' ? $t('Create rich text widget') : $t('Edit rich text widget') }}
            </div>
          </template>
        </HawkModalHeader>
        <HawkModalContent>
          <WysiwygEditorElement
            name="content"
            rules="required"
            :options="{
              editor_enabled: true,
              menu_enabled: true,
              display_images_fullwidth: false,
              resize_config: { quality: 1, maxWidth: 1280, maxHeight: 800 },
              placeholder_text: $t('Enter widget content'),
              plugins: ['tables', 'images'],
              editor_classes: 'rounded-b',
              editor_styles: state.form_data.is_transparent_background ? '' : `background-color: ${state.background_color}`,
            }"
            class="mb-6"
          />
          <ToggleElement
            name="is_vertically_centered"
            :label="$t('Center content vertically')"
            :default="false"
            class="mb-6"
          />
          <ToggleElement
            name="is_transparent_background"
            :label="$t('Transparent background')"
            :default="true"
          />
          <div v-if="!state.form_data.is_transparent_background" class="grid grid-cols-12 mt-6">
            <div class="col-span-4 text-sm font-medium text-gray-700">
              {{ $t('Background color') }}
            </div>
            <div class="col-span-8">
              <BiColorPicker
                type="outlined"
                :active-color="state.background_color"
                :append-white-to-the-start="true"
                @color-selected="state.background_color = $event"
              />
            </div>
          </div>
        </HawkModalContent>
        <HawkModalFooter>
          <template #right>
            <div class="flex justify-end w-full col-span-full">
              <ButtonElement
                name="cancel"
                class="mr-4"
                :secondary="true"
                @click="emit('close')"
              >
                {{ $t('Cancel') }}
              </ButtonElement>
              <ButtonElement
                name="save"
                submits
              >
                {{ $t('Save') }}
              </ButtonElement>
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
